import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Plus, Upload, Eye, RotateCcw, Box } from 'lucide-react';
import { useAuth } from '../../modules/auth';
import { usePermissions } from '../../modules/auth/hooks/usePermissions';
import { PageCard, SectionLabel, StatBlock, TOKENS, ConfirmDialog } from '../../components/design-system';
import { listStock, getStockToken, regenerateStockToken } from './api';
import { StockStatus, STOCK_STATUS_LABEL } from './types';
import { CreateStockModal } from './components/CreateStockModal';
import { ImportCsvModal } from './components/ImportCsvModal';

const PAGE_SIZE = 50;

export const ManufacturerStockPage: FC = () => {
  const { currentUser } = useAuth();
  void currentUser;
  const perms = usePermissions();

  // First manufacturer_id from the list — the page operates on one
  // manufacturer at a time. If a user is linked to multiple, we'd
  // surface a selector here; for the MVP we just pick the first.
  const manufacturerId = perms.manufacturer_ids?.[0];

  const [statusFilter, setStatusFilter] = useState<StockStatus | 'all'>('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [tokenView, setTokenView] = useState<{ id: number; token: string } | null>(null);
  const [regenConfirm, setRegenConfirm] = useState<number | null>(null);

  const list = useQuery(
    ['manufacturer-stock', statusFilter, search, page],
    () => listStock({
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: search.trim() || undefined,
      take: PAGE_SIZE,
      skip: (page - 1) * PAGE_SIZE,
    }),
    { enabled: !!manufacturerId, keepPreviousData: true },
  );

  const qc = useQueryClient();
  const regenMut = useMutation(
    (id: number) => regenerateStockToken(id),
    {
      onSuccess: (data, id) => {
        setRegenConfirm(null);
        setTokenView({ id, token: data.token });
        qc.invalidateQueries(['manufacturer-stock']);
        toast.success('Novo token gerado. O anterior foi revogado.');
      },
      onError: (e: any) => {
        toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`);
      },
    },
  );

  const showToken = async (id: number) => {
    try {
      const { token } = await getStockToken(id);
      setTokenView({ id, token });
    } catch (e: any) {
      toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`);
    }
  };

  // ----- Empty state: user is not a manufacturer -----
  if (!perms.is_manufacturer) {
    return (
      <PageCard>
        <SectionLabel>Estoque</SectionLabel>
        <div style={{ padding: 16, color: TOKENS.color.muted, fontSize: 13 }}>
          Você não está cadastrado como usuário de nenhum fabricante. Solicite ao administrador da
          plataforma que vincule seu usuário ao fabricante via <code>manufacturer_users</code>.
        </div>
      </PageCard>
    );
  }

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const counts = rows.reduce<Record<StockStatus | 'total', number>>(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1, total: acc.total + 1 }),
    { total: 0, pending: 0, activated: 0, revoked: 0 },
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <PageCard>
        <SectionLabel>Fabricação</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <Box size={20} color={TOKENS.color.action} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text, margin: 0 }}>Estoque</h1>
        </div>
        <p style={{ marginTop: 8, fontSize: 13, color: TOKENS.color.muted, lineHeight: 1.5, marginBottom: 12 }}>
          Cadastre as caixas em produção. Cada caixa recebe um token JWT pra programar no firmware.
          Quando a caixa for entregue, um administrador da empresa destino vai ativar o estoque pra
          que a caixa entre em operação.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: TOKENS.spacing.md }}>
          <StatBlock value={total} label='Total carregadas' tone='neutral' size='sm' />
          <StatBlock value={counts.pending} label='Aguardando ativação' tone='medium' size='sm' />
          <StatBlock value={counts.activated} label='Ativadas' tone='low' size='sm' />
        </div>
      </PageCard>

      <PageCard>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(['all', 'pending', 'activated', 'revoked'] as Array<StockStatus | 'all'>).map((s) => {
              const active = statusFilter === s;
              const label = s === 'all' ? 'Todas' : STOCK_STATUS_LABEL[s];
              return (
                <span key={s} role='button' onClick={() => { setStatusFilter(s); setPage(1); }} style={{
                  padding: '4px 10px', borderRadius: TOKENS.radius.chip, fontSize: 11, fontWeight: 700,
                  letterSpacing: 0.3, textTransform: 'uppercase', cursor: 'pointer',
                  background: active ? TOKENS.color.action : TOKENS.color.surface,
                  color: active ? '#fff' : TOKENS.color.text,
                }}>{label}</span>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button type='button' onClick={() => setImportOpen(true)} style={ghostBtn}>
              <Upload size={14} /> Importar CSV
            </button>
            <button type='button' onClick={() => setCreateOpen(true)} style={primaryBtn}>
              <Plus size={14} /> Nova caixa
            </button>
          </div>
        </div>

        <input
          type='text'
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder='🔍 Buscar por CMB ou bluetooth'
          style={{
            marginTop: TOKENS.spacing.sm,
            width: '100%', padding: '8px 10px', fontSize: 13,
            border: `1px solid ${TOKENS.color.border}`,
            borderRadius: TOKENS.radius.chip,
            background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
          }}
        />

        <div style={{ marginTop: TOKENS.spacing.md }}>
          {list.isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>Carregando…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>
              Nenhuma caixa encontrada com esses filtros.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((r) => (
                <div key={r.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', background: TOKENS.color.surface,
                  borderRadius: TOKENS.radius.chip, fontSize: 12,
                  borderLeft: `3px solid ${
                    r.status === 'activated' ? TOKENS.color.severity.low.text
                    : r.status === 'revoked' ? TOKENS.color.muted
                    : TOKENS.color.severity.medium.text
                  }`,
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontWeight: 700, color: TOKENS.color.text }}>
                      CMB {r.cmb_number} <span style={{ color: TOKENS.color.muted, fontWeight: 500 }}>·</span> {r.bluetooth}
                    </div>
                    <div style={{ fontSize: 11, color: TOKENS.color.muted }}>
                      {r.intended_company_name ? `Destino: ${r.intended_company_name}` : 'Sem destino definido'}
                      {' · '}
                      Criada {new Date(r.created_at).toLocaleDateString('pt-BR')}
                      {r.status === 'activated' && r.activated_to_company_name && ` · Ativada em ${r.activated_to_company_name}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                      padding: '2px 8px', borderRadius: TOKENS.radius.chip, color: '#fff',
                      background: r.status === 'activated' ? TOKENS.color.severity.low.text
                        : r.status === 'revoked' ? TOKENS.color.muted
                        : TOKENS.color.severity.medium.text,
                    }}>
                      {STOCK_STATUS_LABEL[r.status]}
                    </span>
                    {/* "Ver token" funciona enquanto o token não foi
                        revogado — pending OU activated. Útil pra
                        re-programar firmware perdido / refurbish.
                        "Re-gerar token" continua só em pending — uma
                        vez ativada, mudar o jti quebraria o firmware
                        já em uso no campo. */}
                    {(r.status === 'pending' || r.status === 'activated') && !r.token_revoked && (
                      <button type='button' onClick={() => showToken(r.id)} style={iconBtn} title='Ver token'>
                        <Eye size={14} />
                      </button>
                    )}
                    {r.status === 'pending' && (
                      <button type='button' onClick={() => setRegenConfirm(r.id)} style={iconBtn} title='Re-gerar token'>
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: TOKENS.spacing.sm }}>
            <button type='button' onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={ghostBtn}>
              ‹ Anterior
            </button>
            <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>
              {page} / {totalPages} · {total} caixas
            </span>
            <button type='button' onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={ghostBtn}>
              Próxima ›
            </button>
          </div>
        )}
      </PageCard>

      {manufacturerId && (
        <>
          <CreateStockModal manufacturer_id={manufacturerId} open={createOpen} onClose={() => setCreateOpen(false)} />
          <ImportCsvModal manufacturer_id={manufacturerId} open={importOpen} onClose={() => setImportOpen(false)} />
        </>
      )}

      {/* Token view modal */}
      {tokenView && (
        <div style={modalOverlay} onClick={() => setTokenView(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560 }}>
            <PageCard>
              <SectionLabel>Token do firmware</SectionLabel>
              <div style={{
                marginTop: 8, padding: 10,
                background: TOKENS.color.surface, border: `1px solid ${TOKENS.color.border}`,
                borderRadius: TOKENS.radius.chip,
                fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all',
                maxHeight: 200, overflowY: 'auto', color: TOKENS.color.text,
              }}>
                {tokenView.token}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button
                  type='button'
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(tokenView.token); toast.success('Copiado.'); }
                    catch { toast.error('Selecione o texto manualmente.'); }
                  }}
                  style={primaryBtn}
                >Copiar</button>
                <button type='button' onClick={() => setTokenView(null)} style={ghostBtn}>Fechar</button>
              </div>
            </PageCard>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={regenConfirm !== null}
        title='Re-gerar token?'
        description='O token anterior será revogado imediatamente e qualquer firmware programado com ele vai parar de autenticar. Use apenas se a caixa ainda não foi entregue.'
        confirmLabel='Re-gerar'
        tone='danger'
        loading={regenMut.isLoading}
        onCancel={() => setRegenConfirm(null)}
        onConfirm={() => regenConfirm && regenMut.mutate(regenConfirm)}
      />
    </div>
  );
};

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.action, color: '#fff', border: 'none',
  padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '6px 12px', fontSize: 12, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: TOKENS.color.card, color: TOKENS.color.muted,
  border: `1px solid ${TOKENS.color.border}`,
  width: 26, height: 26, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const modalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px', zIndex: 1000, overflowY: 'auto',
};

export default ManufacturerStockPage;
